<template>
  <slot
    v-if="custom"
    v-bind="{
      href,
      route,
      isActive,
      routeIsActive: routeIsActiveRef,
      routeIsExactActive: routeIsExactActiveRef,
      pathMatches,
      queryMatches,
      navigate,
    }"
  />
  <a
    v-else
    v-tooltip="tooltip"
    :href="disabled ? null : href"
    class="pb-link"
    :class="{
      [props.activeClass]: isActive,
      [props.disabledClass]: disabled,
    }"
    @click="openLink"
  >
    <slot />
  </a>
</template>

<script setup>
import {
  computed,
  // mergeProps,
  toRef,
  // useAttrs,
} from 'vue'
import { useLink, useRoute } from 'vue-router'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'

/*
This component is used to override the new isActive/isExactActive behavior of Vue Router 4.

It doesn't match based on the path anymore, but on the nesting of the routes.

This component creates RouterLinks with the original behavior.

See https://github.com/vuejs/rfcs/blob/master/active-rfcs/0028-router-active-link.md
*/

const props = defineProps({
  to: { type: [Object, String], required: true },
  activeClass: { type: String, default: 'pb-link-active' },
  exact: { type: Boolean, default: false },
  custom: { type: Boolean, default: false },
  tooltip: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  disabledClass: { type: String, default: 'pb-link-disabled' },
  /**
   * True if the link should match queries when computing "isActive"
   */
  matchQuery: { type: [Boolean, Array], default: false },
})

const currentRoute = useRoute()

/**
 * From https://github.com/vuejs/rfcs/blob/master/active-rfcs/0028-router-active-link.md
 * @param {*} outter - Queries in which we want to search
 * @param {*} inner - Queries we're searching for
 */
function matchQueries(outter, inner) {
  const { matchQuery } = props
  const keys = Array.isArray(matchQuery) ? matchQuery : Object.keys(inner)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    const innerValue = inner[key]
    const outterValue = outter[key]
    if (typeof innerValue === 'string') {
      if (innerValue !== outterValue) return false
    } else if (
      !Array.isArray(outterValue)
      || outterValue.length !== innerValue.length
      || innerValue.some((value, i) => value !== outterValue[i])
    ) {
      return false
    }
  }
  return true
}

const {
  href,
  isActive: routeIsActiveRef,
  isExactActive: routeIsExactActiveRef,
  route,
  navigate,
} = useLink({
  to: toRef(props, 'to'),
})

async function openLink(...args) {
  if (!props.disabled) {
    return navigate(...args)
  }
  return undefined
}

const pathMatches = computed(() => currentRoute.path.startsWith(route.value.path))
const queryMatches = computed(() => matchQueries(
  currentRoute.query,
  route.value.query,
))

const isActive = computed(() => {
  const { exact, matchQuery } = props
  const routeIsExactActive = routeIsExactActiveRef.value
  const matchingPath = pathMatches.value
  const matchingQuery = matchQuery ? queryMatches.value : true
  if (exact) {
    return routeIsExactActive
  }
  return matchingPath && matchingQuery
})

/*
// Use linkActiveClass & linkExactActiveClass from vue-router options ?
const router = useRouter()
router.options.linkActiveClass
router.options.linkExactActiveClass
*/
</script>

<script>
export default {
  compatConfig: {
    INSTANCE_LISTENERS: false,
  },
}
</script>
